import { TextListGroupValue } from 'particles/TextInputs/TextList/Group'
import { BaseAction } from '../index'
import { CampaignPreviewActions as CampaignInformationActions } from './action/campaign'
import { MediaData } from 'components/MediaViewer'
import { AppPreviewActions } from './action'
import { MediaState } from 'components/AppPreview/common/MediaPreview'

export interface ValueObject {
    [key: string]: string | boolean | number | undefined
    emptyValue: string
    isInline?: boolean
    isUrl?: boolean
    concatSymbol?: string
}

export interface InterpolationListProps {
    template: string
    values?: ValueObject[]
}

export type AppPreviewState = {
    /** Placeholder key (e.g. purchase_locations) currently focused/edited in the form; used to highlight that segment in the preview. */
    activeInterpolationKey: string | null
    campaignInformation: {
        title: string
        categories: number[]
        images: MediaData[]
        registration_enable: boolean
        use_default_image: boolean
    }
    purchaseProduct: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            description?: string
            examplePhotos?: MediaState[]
            purchaseLocations?: TextListGroupValue[]
            itemsToReview?: string[]
        }
        interpolationListData: {
            howToBuy?: InterpolationListProps
        }
    }
    igReelsTask: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            captionDos?: string
            captionDonts?: string
            captionHashtags?: TextListGroupValue[]
            exampleMediaDescription?: string
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            mediaDos?: InterpolationListProps
            captionDos?: InterpolationListProps
        }
    }
    igStoryRepost: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            description?: string
            captionHashtags?: TextListGroupValue[]
            profileTags?: TextListGroupValue[]
            exampleMediaDescription?: string
            exampleMedia?: MediaState[]
        }
        interpolationListData: {
            mediaDos?: InterpolationListProps
        }
    }
    tiktokRepost: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            description?: string
            urlBlockTitle?: string
            mediaBlockTitle?: string
            mediaBlockMaxPhotos?: number
        }
    }
    tikTok: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            captionDos?: string
            captionDonts?: string
            captionHashtags?: TextListGroupValue[]
            profileTags?: TextListGroupValue[]
            exampleMediaDescription?: string
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            mediaDos?: InterpolationListProps
            captionDos?: InterpolationListProps
        }
    }
    igStoryNonUgc: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            captionHashtags?: TextListGroupValue[]
            profileTags?: TextListGroupValue[]
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            mediaDos?: InterpolationListProps
        }
    }
    igStoryUgc: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            captionHashtags?: TextListGroupValue[]
            profileTags?: TextListGroupValue[]
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            mediaDos?: InterpolationListProps
        }
    }
    igPost: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            submissionRequirementDescription?: string
            mediaDos?: string
            mediaDonts?: string
            captionDonts?: string
            captionHashtags?: TextListGroupValue[]
            exampleMediaDescription?: string
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            captionDos?: InterpolationListProps
            mediaDos?: InterpolationListProps
        }
    }
    reviewTextImage: {
        step1: {
            thingsToNote?: string
            taskTitle?: string
            stepTitle?: string
            title?: string
            description?: string
            mediaBlockTitle?: string
            mediaBlockMaxPhotos?: number
            textBlockTitle?: string
            review_platform?: string
        }
        interpolationListData: {
            description?: InterpolationListProps
            stepTitle?: InterpolationListProps
        }
    }
    downloadApp: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            mediaBlockTitle?: string
            mediaBlockMaxPhotos?: number
            textBlockTitle?: string
            app_name?: string
        }
        interpolationListData: {
            description?: InterpolationListProps
        }
    }
    follow: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            description?: string
            mediaBlockTitle?: string
            mediaBlockMaxPhotos?: number
            textBlockTitle?: string
            follow_platform?: string
        }
        interpolationListData: {
            description?: InterpolationListProps
            taskTitle?: InterpolationListProps
        }
    }
    videoCreation: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            videoDosTitle?: string
            videoDos?: string
            videoDontsTitle?: string
            videoDonts?: string
            videoOverlayDosTitle?: string
            videoTextOverlayDos?: string
            videoOverlayDontsTitle?: string
            videoTextOverlayDonts?: string
            exampleVideos?: MediaState[]
        }
        interpolationListData: {
            videoDos?: InterpolationListProps
            videoTextOverlayDos?: InterpolationListProps
            taskTitle?: InterpolationListProps
        }
    }
    photoCreation: {
        step1: {
            taskTitle?: string
            stepTitle?: string
            title?: string
            photoDosTitle?: string
            photoDos?: string
            photoDontsTitle?: string
            photoDonts?: string
            examplePhotos?: MediaState[]
        }
        interpolationListData: {
            photoDos?: InterpolationListProps
            photoDonts?: InterpolationListProps
        }
    }
}

const initialState: AppPreviewState = {
    activeInterpolationKey: null,
    campaignInformation: {
        title: '',
        categories: [],
        images: [],
        registration_enable: false,
        use_default_image: false,
    },
    purchaseProduct: {
        step1: {},
        interpolationListData: {},
    },
    igReelsTask: {
        step1: {},
        interpolationListData: {},
    },
    igStoryRepost: {
        step1: {},
        interpolationListData: {},
    },
    tiktokRepost: {
        step1: {},
    },
    tikTok: {
        step1: {},
        interpolationListData: {},
    },
    igStoryNonUgc: {
        step1: {},
        interpolationListData: {},
    },
    igStoryUgc: {
        step1: {},
        interpolationListData: {},
    },
    igPost: {
        step1: {},
        interpolationListData: {},
    },
    reviewTextImage: {
        step1: {},
        interpolationListData: {},
    },
    downloadApp: {
        step1: {},
        interpolationListData: {},
    },
    follow: {
        step1: {},
        interpolationListData: {},
    },
    videoCreation: {
        step1: {},
        interpolationListData: {},
    },
    photoCreation: {
        step1: {},
        interpolationListData: {},
    },
}

export const appPreviewReducer = (state: AppPreviewState = initialState, action: BaseAction) => {
    switch (action.type) {
        case AppPreviewActions.UPDATE_PURCHASE_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                purchaseProduct: {
                    ...state.purchaseProduct,
                    step1: {
                        ...state.purchaseProduct.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case CampaignInformationActions.UPDATE_CAMPAIGN_INFORMATION_DATA: {
            return Object.assign({}, state, {
                ...state,
                campaignInformation: {
                    ...state.campaignInformation,
                    ...action.payload,
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_REELS_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                igReelsTask: {
                    ...state.igReelsTask,
                    step1: {
                        ...state.igReelsTask.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_REPOST_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryRepost: {
                    ...state.igStoryRepost,
                    step1: {
                        ...state.igStoryRepost.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_REPOST_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryRepost: {
                    ...state.igStoryRepost,
                    interpolationListData: {
                        ...state.igStoryRepost.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_TIKTOK_REPOST_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                tiktokRepost: {
                    ...state.tiktokRepost,
                    step1: {
                        ...state.tiktokRepost.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.CLEAR_APP_PREVIEW_DATA: {
            return Object.assign({}, state, {
                ...state,
                ...initialState,
            })
        }
        case AppPreviewActions.SET_ACTIVE_INTERPOLATION_KEY: {
            return Object.assign({}, state, {
                ...state,
                activeInterpolationKey: action.payload,
            })
        }
        case AppPreviewActions.UPDATE_IG_REELS_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                igReelsTask: {
                    ...state.igReelsTask,
                    interpolationListData: {
                        ...state.igReelsTask.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_PURCHASE_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                purchaseProduct: {
                    ...state.purchaseProduct,
                    interpolationListData: {
                        ...state.purchaseProduct.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_TIKTOK_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                tikTok: {
                    ...state.tikTok,
                    step1: {
                        ...state.tikTok.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_TIKTOK_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                tikTok: {
                    ...state.tikTok,
                    interpolationListData: {
                        ...state.tikTok.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_NON_UGC_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryNonUgc: {
                    ...state.igStoryNonUgc,
                    step1: {
                        ...state.igStoryNonUgc.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_NON_UGC_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryNonUgc: {
                    ...state.igStoryNonUgc,
                    interpolationListData: {
                        ...state.igStoryNonUgc.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_UGC_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryUgc: {
                    ...state.igStoryUgc,
                    step1: {
                        ...state.igStoryUgc.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_STORY_UGC_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                igStoryUgc: {
                    ...state.igStoryUgc,
                    interpolationListData: {
                        ...state.igStoryUgc.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_POST_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                igPost: {
                    ...state.igPost,
                    step1: {
                        ...state.igPost.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_IG_POST_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                igPost: {
                    ...state.igPost,
                    interpolationListData: {
                        ...state.igPost.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_REVIEW_TEXT_IMAGE_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                reviewTextImage: {
                    ...state.reviewTextImage,
                    step1: {
                        ...state.reviewTextImage.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_REVIEW_TEXT_IMAGE_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                reviewTextImage: {
                    ...state.reviewTextImage,
                    interpolationListData: {
                        ...state.reviewTextImage.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_DOWNLOAD_APP_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                downloadApp: {
                    ...state.downloadApp,
                    step1: {
                        ...state.downloadApp.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_DOWNLOAD_APP_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                downloadApp: {
                    ...state.downloadApp,
                    interpolationListData: {
                        ...state.downloadApp.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_FOLLOW_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                follow: {
                    ...state.follow,
                    step1: {
                        ...state.follow.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_FOLLOW_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                follow: {
                    ...state.follow,
                    interpolationListData: {
                        ...state.follow.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_VIDEO_CREATION_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                videoCreation: {
                    ...state.videoCreation,
                    step1: {
                        ...state.videoCreation.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_VIDEO_CREATION_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                videoCreation: {
                    ...state.videoCreation,
                    interpolationListData: {
                        ...state.videoCreation.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_PHOTO_CREATION_STEP1_DATA: {
            return Object.assign({}, state, {
                ...state,
                photoCreation: {
                    ...state.photoCreation,
                    step1: {
                        ...state.photoCreation.step1,
                        ...action.payload,
                    },
                },
            })
        }
        case AppPreviewActions.UPDATE_PHOTO_CREATION_PLACEHOLDER_DATA: {
            return Object.assign({}, state, {
                ...state,
                photoCreation: {
                    ...state.photoCreation,
                    interpolationListData: {
                        ...state.photoCreation.interpolationListData,
                        ...action.payload,
                    },
                },
            })
        }

        default:
            return state
    }
}
